import type { LoqateAddressDetailsItem } from "@simplybusiness/mobius";
import type { MappedFields } from "./AddressLookupQuestion";

/**
 * Transforms Loquate address data into a format required by Chopin.
 * Maps address fields based on configured field mappings prepended with the question ID and adds a formatted label.
 * Returns a flat object with mapped fields, used in FormData submission, and a nested object with the original address data, for Ajax requests.
 *
 * @param questionId - Unique identifier for the address question field
 * @param questionName - Name of the address question field
 * @param addressData - Address details received from Loquate API
 * @param mappedFields - Configuration array defining how address fields should be mapped
 * @returns Record<string, string> - Transformed address data with mapped fields
 */
const POSTCODE_FIELD = "PostalCode";
const ADDRESS_FIELD_MAP = {
  "1": "Line1",
  "2": "Line2",
  town: "City",
  postcode: POSTCODE_FIELD,
} as const;

type TransformAddressData = {
  questionName: string;
  addressData: LoqateAddressDetailsItem;
  mappedFields: MappedFields[];
};

// Remove any hyphenated part from the postal code (e.g., "90210-1234" becomes "90210")
const cleanPostalCode = (postalCode: string): string => {
  return postalCode.split("-")[0];
};

export const transformAddressData = ({
  questionName,
  addressData,
  mappedFields,
}: TransformAddressData): Record<string, string> =>
  mappedFields.reduce<Record<string, string>>(
    (acc, { associatedQuestionId }) => {
      const lowerCaseId = associatedQuestionId.toLowerCase();

      for (const [key, value] of Object.entries(ADDRESS_FIELD_MAP)) {
        if (lowerCaseId.includes(key)) {
          const data = addressData[value] || "";

          acc[associatedQuestionId] =
            value === POSTCODE_FIELD ? cleanPostalCode(data) : data;
          break;
        }
      }

      return acc;
    },
    {
      [questionName]: addressData.Label?.replace(/\n/g, ", "),
    },
  );
